import { useContext, useState, useEffect } from 'react';
import clonedeep from 'lodash.clonedeep';
import { Table, TableContainer, TableCell, TableRow, TableHead, TableBody, Typography, Select, MenuItem, IconButton, Tooltip, TextField, CircularProgress, Input, Chip } from '@material-ui/core';
import SaveIcon from '@material-ui/icons/Save';
import { TablesContext } from '../TablesContext';
import { SettingsContext } from '../../../../contexts';
import { AppSettingsType, AppSettingNames, SapTableNames, LocalTableNames } from '../../../../../helpers';
import { makeStyles } from '@material-ui/core/styles';
import { DbType } from '../../../../../helpers/Common';

const useStyles = makeStyles({
    field: {
        width: '100%'
    },
    chip: {
        margin: 2,
    },
    chips: {
        display: 'flex',
        flexWrap: 'wrap',
    },
});

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
    PaperProps: {
        style: {
            maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
            width: 250
        }
    }
};

export const SettingsTable = (props) => {
    const classes = useStyles();

    const {
        state,
        updateAppSetting,
        isUpdatingAppSetting
    } = useContext(TablesContext);

    const {
        appSettingsS4,
        appSettingsECC
    } = useContext(SettingsContext);

    const [updatedAS, setUpdatedAS] = useState([])
    const [loadingKey, setLoadingKey] = useState(null);

    const appSettingType = props.localAppSettings ? AppSettingsType.Local : props.sapAppSettings ? AppSettingsType.Sap : 2;

    const getLocalSettingsTableOptions = (tableName) => {
        if (state && state.tables && state.tables.length > 0) {
            const table = state.tables.find(x => x.name === tableName);

            if (table) {
                const childValues = props.dbType === DbType.ECC ?
                    table.childValues_ECC :
                    table.childValues_S4;

                return childValues.map(x => ({
                    value: x.tableValueId,
                    label: x.value,
                    externalKey: x.externalKey
                }));
            }
        }

        return [{ value: "", label: "No available values" }];
    };

    const getLocalSettingsCountriesOptions = () => {
        if (state && state.tables && state.tables.length > 0) {
            const table = state.tables.find(x => x.name === SapTableNames.Countries);

            if (table) {
                const childValues = props.dbType === DbType.ECC ?
                    table.childValues_ECC :
                    table.childValues_S4;

                return childValues.map(x => ({
                    value: x.tableValueId,
                    label: `${x.externalKey} - ${x.value}`
                }));
            }
        }

        return [{ value: "", label: "No available values" }];
    };

    const getLocalSettingsTableValueOrEmpty = (tableName, valueId) => {
        if (state && state.tables && state.tables.length > 0) {
            const table = state.tables.find(x => x.name === tableName);

            if (table) {
                const childValues = props.dbType === DbType.ECC ?
                    table.childValues_ECC :
                    table.childValues_S4;

                const child = childValues.find(x => x.tableValueId === valueId);

                if (child)
                    return child.tableValueId;
            }
        }

        return "";
    };

    const getAppSettingTitle = (appSettingkey) => {
        switch (appSettingkey) {
            case AppSettingNames.ItalyCountryId:
                return "SDI Country";
            case AppSettingNames.PerfumesBusinessCategoryId:
                return "Perfumes Business Category";
            case AppSettingNames.AccGroupNoBankInfoIds:
                return "Account Group with no bank info";
            default:
                return appSettingkey;
        }
    };

    const getEditField = (externalKey) => {
        const targetAppSetting = updatedAS.find(x => x.appSettingKey === externalKey);

        switch (externalKey) {
            case AppSettingNames.AccountStatement:
            case AppSettingNames.Authorization:
            case AppSettingNames.ExchangeRateType:
            case AppSettingNames.FlagPaymentHistory:
            case AppSettingNames.PreviusAccountNumber:
            case AppSettingNames.PricingProcedure:
            case AppSettingNames.StatisticsGroup:
            case AppSettingNames.DivisionPerfumes:
            case AppSettingNames.DivisionJWA:
            case AppSettingNames.DeliveringPlant:
            case AppSettingNames.AccGrSH:
                return (
                    <TextField
                        className={classes.field}
                        placeholder={targetAppSetting.appSettingKey}
                        value={targetAppSetting ? targetAppSetting.value : ""}
                        onChange={(e) => onChangeHandler(e.target.value, externalKey)}
                        variant="outlined"
                    />
                );

            case AppSettingNames.ItalyCountryId:
                const countries = getLocalSettingsCountriesOptions();
                const country = countries.find(x => x.value === parseInt(targetAppSetting.value));

                return (
                    <Select
                        className={classes.field}
                        value={
                            country ?
                                getLocalSettingsTableValueOrEmpty(SapTableNames.Countries, country.value) :
                                ""
                        }
                        onChange={(e) => onChangeHandler(e.target.value, externalKey)}
                    >
                        {
                            countries.map((item, index: number): JSX.Element =>
                                <MenuItem
                                    key={index}
                                    value={item.value}
                                >
                                    {item.label}
                                </MenuItem>
                            )
                        }
                    </Select>
                );

            case AppSettingNames.PerfumesBusinessCategoryId:
                const businessCategories = getLocalSettingsTableOptions(LocalTableNames.BusinessCategories);
                const buCategory = businessCategories.find(x => x.value === parseInt(targetAppSetting.value));

                return (
                    <Select
                        className={classes.field}
                        value={
                            buCategory ?
                                getLocalSettingsTableValueOrEmpty(LocalTableNames.BusinessCategories, buCategory.value) :
                                ""
                        }
                        onChange={(e) => onChangeHandler(e.target.value, externalKey)}
                    >
                        {
                            businessCategories.map((item, index: number): JSX.Element =>
                                <MenuItem
                                    key={index}
                                    value={item.value}
                                >
                                    {item.label}
                                </MenuItem>
                            )
                        }
                    </Select>
                );

            case AppSettingNames.AccGroupNoBankInfoIds:
                const accGroups = getLocalSettingsTableOptions(SapTableNames.AccountGroups);
                //const accGroup = accGroups.find(x => x.value === parseInt());
                
                return (
                    <Select
                        className={classes.field}
                        labelId="demo-mutiple-chip-label"
                        id="demo-mutiple-chip"
                        multiple
                        value={targetAppSetting.value ? targetAppSetting.value.split(',').map(x => parseInt(x)) : []}
                        onChange={(e) => onChangeHandler(e.target.value, externalKey)}
                        input={<Input id="select-multiple-chip" />}
                        MenuProps={MenuProps}
                        renderValue={(selected: any): JSX.Element =>
                            <div className={classes.chips}>
                                {
                                    selected.map((value, index: number): JSX.Element => {
                                        const accGroup = accGroups.find(x => x.value === parseInt(value));
                                        
                                        return (
                                            <Chip
                                                key={index}
                                                label={accGroup ? `${accGroup.externalKey} - ${accGroup.label}` : ""}
                                                className={classes.chip}
                                            />
                                        );
                                    })
                                }
                            </div>
                        }
                    >
                        {
                            accGroups.map((item, index: number): JSX.Element =>
                                <MenuItem key={index} value={item.value}>
                                    {`${item.externalKey} - ${item.label}`}
                                </MenuItem>
                            )
                        }
                    </Select>
                )
            // return (
            //     <Select
            //         className={classes.field}
            //         value={accGroup ? getTableValueOrEmpty(SapTableNames.AccountGroups, accGroup.value) : ""}
            //         onChange={(e) => onChangeHandler(e.target.value, externalKey)}>
            //         {accGroups.map((item, index) => {
            //             return (
            //                 <MenuItem key={index} value={item.value}>{`${item.externalKey} - ${item.label}`}</MenuItem>
            //             );
            //         })}
            //     </Select>
            // );

            default:
                return (
                    <Typography>
                        Not supported
                    </Typography>
                );
        }
    };

    const saveClickHandler = async (externalKey) => {
        const appSetting = updatedAS.find(x => x.appSettingKey === externalKey);

        if (appSetting) {
            setLoadingKey(appSetting.appSettingKey);
            await updateAppSetting({ appSettingId: appSetting.appSettingId, value: appSetting.value, dbType: props.dbType });
            setLoadingKey(null);
        }
    };

    const onChangeHandler = (value, externalKey) => {
        const newAppSettings = [].concat(updatedAS);
        const targetAppSetting = newAppSettings.find(x => x.appSettingKey === externalKey);

        if (targetAppSetting) {
            if (externalKey === AppSettingNames.AccGroupNoBankInfoIds)
                targetAppSetting.value = value.join(',');
            else
                targetAppSetting.value = value;

            setUpdatedAS(newAppSettings)
        }
    };

    useEffect(() => {
        let as;

        if (props.dbType === DbType.ECC)
            as = clonedeep(appSettingsECC);
        else
            as = clonedeep(appSettingsS4);
            
        setUpdatedAS(as);
    }, [])

    return (
        <>
            <TableContainer>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell style={{ width: "50%" }}>Name</TableCell>
                            <TableCell style={{ maxWidth: "30%" }}>Value</TableCell>
                            <TableCell style={{ width: "20%" }}></TableCell>
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {
                            updatedAS &&
                            updatedAS.length > 0 &&
                            updatedAS
                                .filter(x => x.type === appSettingType)
                                .map((as, index: number): JSX.Element =>
                                    <TableRow key={index}>
                                        <TableCell>
                                            <Typography>
                                                {getAppSettingTitle(as.appSettingKey)}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            {getEditField(as.appSettingKey)}
                                        </TableCell>
                                        <TableCell align="right">
                                            <Tooltip title="Save">
                                                {
                                                    as.appSettingKey === loadingKey ?
                                                    <CircularProgress /> :
                                                    <IconButton
                                                        onClick={async () => await saveClickHandler(as.appSettingKey)}
                                                        disabled={isUpdatingAppSetting}
                                                        aria-label="save"
                                                        color="primary">
                                                        <SaveIcon />
                                                    </IconButton>
                                                }
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                )
                        }
                    </TableBody>
                </Table>
            </TableContainer>
        </>
    );
}